import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import {
  eventObject,
  eventAction,
  videoPlayerNames,
  analyticsVideoTypes,
  milestoneSecInterval,
  milestonePercentPoints,
  eventCategory,
} from '@/constants/analytics'
import { YouTubePlayerType } from '@/containers/YouTube/YouTubePlayer'
import useStore from '@/store/useStore'
import { VideoViewedType } from '@/types/analyticsTypes'
import { eventTypes, tileTypes } from '@/types/servicesTypes/configTypes'
import {
  getAnalyticsUserData,
  getEpgObject,
  getPercentageOfViewed,
  sendAnalyticsEvent,
  sendMilestonePercentAnalyticsEvent,
  sendMilestoneSecAnalyticsEvent,
} from '@/utils/analyticsUtils'
import { getPercentageRangeOfViewed, sendEventToGTAG } from '@/utils/utils'

export function usePlayerAnalytics(
  videoId: string,
  isVideoStarted: boolean,
  isVideoPlaying: boolean,
  videoElementRef: RefObject<HTMLVideoElement>,
  videoData?: VideoViewedType,
  videoSrc?: string,
  activeChannelId?: string,
  isMuted?: boolean,
  isBlocked?: boolean,
  withUserInteraction = true,
) {
  const currentEvent = useStore((state) => state.common.currentEvent)
  const [currentVideoId, setCurrentVideoId] = useState<string>()
  const isStream = videoData?.type === eventTypes.CHANNEL

  useEffect(() => {
    if (isBlocked || !videoId || isMuted) {
      return setCurrentVideoId('')
    }
    setCurrentVideoId(videoId)
  }, [videoId, isBlocked, isMuted])

  const sendVideoStartedAnalyticsEvent = useCallback(() => {
    const videoDataType = videoData?.type
    const isAd =
      videoDataType !== eventTypes.VOD && videoDataType !== eventTypes.CHANNEL
    if (videoData) {
      let epg
      if (currentEvent && !isAd) {
        epg = getEpgObject(currentEvent)
      }
      const user = getAnalyticsUserData()

      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.START,
        video: {
          id: videoData.id,
          name: videoData?.media_name || videoData.id,
          playerName: videoPlayerNames.HLS,
          autoPlay: !withUserInteraction,
          type: isStream ? analyticsVideoTypes.STREAM : analyticsVideoTypes.VOD,
          length: isStream
            ? 0
            : Math.round(videoElementRef.current?.duration || 0),
          link: videoData.url,
          epg: epg,
          isAd: isAd,
          adDetails: {},
          partnerIds: videoData.partnerIds,
        },
        user,
      })
    }
  }, [videoData, currentEvent, withUserInteraction])

  const sendVideoStartedToGTAG = useCallback(() => {
    const videoDataType = videoData?.type
    const videoType =
      videoDataType === eventTypes.VOD
        ? eventTypes.VOD
        : videoDataType === eventTypes.CHANNEL
        ? 'Live'
        : tileTypes.AD

    if (videoData) {
      sendEventToGTAG(eventCategory.VIDEO_STARTED, {
        category: !activeChannelId ? currentEvent?.categoryId : undefined,
        type: videoType,
        url: videoData.url,
        format: videoData?.format,
        event_name: !activeChannelId ? currentEvent?.name : undefined,
        channel_name: activeChannelId ? videoData.media_name : undefined,
        channel_id: activeChannelId,
        media_name:
          videoData.type === tileTypes.MEDIA
            ? videoData?.media_name
            : undefined,
        ad_name:
          videoData.type === tileTypes.AD ? videoData.media_name : undefined,
      })
    }
  }, [videoData, currentEvent])

  useVideoAnalytics({
    videoId: currentVideoId,
    isVideoStarted,
    isVideoPlaying,
    videoElementRef,
    videoData,
    isStream,
    sendVideoStartedAnalyticsEvent,
    sendVideoStartedToGTAG,
    activeChannelId,
  })

  const sendFullScreenAnalyticsEvent = useCallback(() => {
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.FULLSCREEN_DEFAULT,
      video: {
        id: videoId,
        link: videoSrc,
      },
    })
  }, [videoSrc, videoId])
  const sendBackPressAnalyticsEvent = useCallback(() => {
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.BACKGROUND_CLICK,
      video: {
        id: videoData?.id,
        link: videoSrc,
      },
    })
  }, [videoSrc, videoId])

  const sendVideoLoadingAnalyticsEvent = useCallback(() => {
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.LOADING,
      video: {
        id: videoId,
        link: videoSrc,
      },
    })
  }, [videoId, videoSrc])

  const sendVideoErrorAnalyticsEvent = useCallback(
    (errorType: string, errorMsg?: string) => {
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.ERROR,
        video: {
          id: videoId,
          link: videoSrc,
          errorType,
          errorMsg,
        },
      })
    },
    [videoId, videoSrc],
  )
  return {
    sendFullScreenAnalyticsEvent,
    sendBackPressAnalyticsEvent,
    sendVideoLoadingAnalyticsEvent,
    sendVideoErrorAnalyticsEvent,
  }
}

export function useVideoAnalytics({
  videoId,
  isVideoStarted,
  isVideoPlaying,
  videoElementRef,
  videoData,
  isStream,
  sendVideoStartedAnalyticsEvent,
  isYouTubePlayer = false,
  sendVideoStartedToGTAG,
  activeChannelId,
}: {
  videoId?: string
  isVideoStarted: boolean
  isVideoPlaying: boolean
  videoElementRef: RefObject<HTMLVideoElement | YouTubePlayerType>
  videoData?: VideoViewedType
  isStream: boolean
  sendVideoStartedAnalyticsEvent: () => void
  isYouTubePlayer?: boolean
  sendVideoStartedToGTAG?: () => void
  activeChannelId?: string
}) {
  const currentEvent = useStore((state) => state.common.currentEvent)
  const streamWatchTimeInterval = useRef<NodeJS.Timeout | undefined>()
  const streamWatchTime = useRef<number>(0)
  const currentPercentPointIndex = useRef<number>(0)

  useEffect(() => {
    if (videoId && isVideoStarted) {
      sendVideoStartedAnalyticsEvent()
      sendVideoStartedToGTAG && sendVideoStartedToGTAG()
    }
    return () => {
      if (videoId && isVideoStarted) {
        sendVideoStoppedAnalyticsEvent()
        sendVideoStartedToGTAG && sendVideoViewedToGTAG()
      }
    }
  }, [videoId, isVideoStarted])

  useEffect(() => {
    return () => {
      resetTimers()
    }
  }, [])

  const sendVideoStoppedAnalyticsEvent = useCallback(() => {
    if (videoData) {
      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action: eventAction.STOP,
        video: {
          id: videoData.id,
          link: videoData.url,
        },
      })
    }
  }, [videoData])

  const sendVideoViewedToGTAG = useCallback(() => {
    if (videoData) {
      const videoDataType = videoData?.type
      const videoType =
        videoDataType === eventTypes.VOD
          ? eventTypes.VOD
          : videoDataType === eventTypes.CHANNEL
          ? 'Live'
          : tileTypes.AD
      const watchTime = Math.ceil(streamWatchTime.current || 0)

      const playerInfo = isYouTubePlayer
        ? (videoElementRef as RefObject<YouTubePlayerType>).current?.playerInfo
        : (videoElementRef.current as HTMLVideoElement)
      const viewingPercentage = getPercentageRangeOfViewed(
        playerInfo?.duration,
        playerInfo?.currentTime,
      )

      sendEventToGTAG(eventCategory.VIDEO_VIEWED, {
        category: !activeChannelId ? currentEvent?.categoryId : undefined,
        type: videoType,
        url: videoData?.url,
        format: videoData?.format,
        event_name: !activeChannelId ? currentEvent?.name : undefined,
        channel_name: activeChannelId ? videoData.media_name : undefined,
        channel_id: activeChannelId,
        media_name:
          videoData?.type === tileTypes.MEDIA
            ? videoData?.media_name
            : undefined,
        ad_name:
          videoData?.type === tileTypes.AD ? videoData?.media_name : undefined,
        watch_time: watchTime,
        video_viewing_percentage:
          videoType !== 'Live' ? viewingPercentage : undefined,
      })
    }
  }, [videoData, currentEvent])

  const sendMilestoneEvent = useCallback(
    (time: number) => {
      if (!videoData) {
        return
      }
      if (isStream) {
        time % milestoneSecInterval || sendMilestoneSecAnalyticsEvent(videoData)
      } else {
        const playerInfo = isYouTubePlayer
          ? (videoElementRef as RefObject<YouTubePlayerType>).current
              ?.playerInfo
          : (videoElementRef.current as HTMLVideoElement)

        const percentage = getPercentageOfViewed(
          playerInfo?.duration,
          playerInfo?.currentTime,
        )
        const currentPercentPoint =
          milestonePercentPoints[currentPercentPointIndex.current]
        if (percentage >= currentPercentPoint) {
          sendMilestonePercentAnalyticsEvent(currentPercentPoint, videoData)
          currentPercentPointIndex.current++
        }
      }
    },
    [isStream, videoData],
  )

  useEffect(() => {
    if (!videoId || !isVideoStarted) {
      resetTimers()
      return
    }
    if (isVideoPlaying) {
      streamWatchTimeInterval.current = setInterval(() => {
        if (videoElementRef.current) {
          streamWatchTime.current++
          sendMilestoneEvent(streamWatchTime.current)
        }
      }, 1000)
    } else {
      clearStreamWatchInterval()
    }
  }, [isVideoPlaying, videoId, isVideoStarted])

  const clearStreamWatchInterval = () => {
    streamWatchTimeInterval.current &&
      clearInterval(streamWatchTimeInterval.current)
  }
  const resetStreamWatchTime = () => {
    streamWatchTime.current = 0
  }
  const resetCurrentPercentPointIndex = () => {
    currentPercentPointIndex.current = 0
  }
  const resetTimers = () => {
    clearStreamWatchInterval()
    resetStreamWatchTime()
    resetCurrentPercentPointIndex()
  }
}
